/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { fetchAvailableIndicators } from '../api/api';
import { getIndicatorKey } from '../utils/indicatorUtils';
import API_BASE_URL, { WS_BASE_URL } from '../api/config';
import {
    getStudioIndicatorDefinitionKey,
    loadStudioIndicatorState,
    serializeStudioIndicatorState,
    storeStudioIndicatorState,
} from '../utils/studioIndicatorStore';

// Add time normalization helpers
const toSeconds = (t) => {
    if (t == null) return t;
    // If it's already a number, convert ms->s if necessary
    if (typeof t === 'number') {
        return t > 1000000000000 ? Math.floor(t / 1000) : t; // ms to s
    }
    // If it's a Date object
    if (t instanceof Date) {
        return Math.floor(t.getTime() / 1000);
    }
    // If it's a business day or plain object like { year, month, day }
    if (typeof t === 'object') {
        const { year, month, day } = t;
        if (typeof year === 'number' && typeof month === 'number' && typeof day === 'number') {
            return Math.floor(new Date(year, month - 1, day).getTime() / 1000);
        }
        return undefined; // unknown object format
    }
    // If it's a string, try parsing
    if (typeof t === 'string') {
        const ms = Date.parse(t);
        if (!Number.isNaN(ms)) return Math.floor(ms / 1000);
        const asNum = Number(t);
        if (!Number.isNaN(asNum)) return toSeconds(asNum);
    }
    return undefined;
};

const toFiniteNumber = (value) => {
    if (value === null || value === undefined || value === '') return undefined;
    const number = Number(value);
    return Number.isFinite(number) ? number : undefined;
};

function normalizeHistoryCandle(candle) {
    const time = toSeconds(candle?.time);
    const open = toFiniteNumber(candle?.open);
    const high = toFiniteNumber(candle?.high);
    const low = toFiniteNumber(candle?.low);
    const close = toFiniteNumber(candle?.close);
    if (!Number.isFinite(time) || [open, high, low, close].some((value) => value === undefined)) {
        return null;
    }
    return {
        time,
        open,
        high,
        low,
        close,
        volume: toFiniteNumber(candle?.volume)
            ?? toFiniteNumber(candle?.tick_volume)
            ?? toFiniteNumber(candle?.tickVolume),
        tickVolume: toFiniteNumber(candle?.tick_volume)
            ?? toFiniteNumber(candle?.tickVolume),
    };
}

const normalizeIndicatorPayload = (data) => {
    if (Array.isArray(data)) {
        return data.map(p => ({ ...p, time: toSeconds(p.time) }));
    }
    if (data && typeof data === 'object') {
        const out = {};
        Object.keys(data).forEach(key => {
            const arr = data[key];
            out[key] = Array.isArray(arr) ? arr.map(p => ({ ...p, time: toSeconds(p.time) })) : arr;
        });
        return out;
    }
    return data;
};

const INDICATOR_EVENT_TIME_FIELDS = [
    'pivot_start_time',
    'pivot_end_time',
    'confirmed_time',
];

const INDICATOR_EVENT_FIELDS = [
    'id',
    'kind',
    'direction',
    'hidden',
    ...INDICATOR_EVENT_TIME_FIELDS,
    'pivot_start_rsi',
    'pivot_end_rsi',
    'price_start',
    'price_end',
    'bars_between',
    'price_separation_pct',
    'rsi_separation',
];

const normalizeIndicatorEvent = (event) => {
    if (!event || typeof event !== 'object' || event.id == null) return null;
    const normalized = { ...event };
    INDICATOR_EVENT_TIME_FIELDS.forEach((field) => {
        if (!Object.prototype.hasOwnProperty.call(normalized, field)) return;
        normalized[field] = toSeconds(normalized[field]);
    });
    return normalized;
};

const normalizeIndicatorEvents = (events) => {
    const items = Array.isArray(events) ? events : (events ? [events] : []);
    return items.map(normalizeIndicatorEvent).filter(Boolean);
};

const getIndicatorEventPayload = (message) => {
    if (message?.event) return message.event;
    if (message?.events) return message.events;
    if (message?.data) return message.data;
    if (message?.id == null) return null;
    return Object.fromEntries(
        INDICATOR_EVENT_FIELDS
            .filter((field) => Object.prototype.hasOwnProperty.call(message, field))
            .map((field) => [field, message[field]]),
    );
};

function indicatorEventsEqual(a, b) {
    const keys = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);
    return [...keys].every((key) => Object.is(a?.[key], b?.[key]));
}

function mergeIndicatorEvents(existing, incoming) {
    const current = Array.isArray(existing) ? existing : [];
    const additions = normalizeIndicatorEvents(incoming);
    if (additions.length === 0) return current;

    const merged = current.slice();
    const indexById = new Map(
        merged
            .map((event, index) => [event?.id == null ? null : String(event.id), index])
            .filter(([id]) => id != null),
    );
    let changed = false;

    additions.forEach((event) => {
        const id = String(event.id);
        const existingIndex = indexById.get(id);
        if (existingIndex == null) {
            indexById.set(id, merged.length);
            merged.push(event);
            changed = true;
            return;
        }

        const nextEvent = { ...merged[existingIndex], ...event };
        if (!indicatorEventsEqual(merged[existingIndex], nextEvent)) {
            merged[existingIndex] = nextEvent;
            changed = true;
        }
    });

    return changed ? merged : current;
}

function pointValueForKey(point, key) {
    if (!point || !key) return undefined;
    if (Number.isFinite(point[key])) return point[key];
    if (Number.isFinite(point.value)) return point.value;
    if (point.value && typeof point.value === 'object' && Number.isFinite(point.value[key])) {
        return point.value[key];
    }
    if (point.outputs && typeof point.outputs === 'object' && Number.isFinite(point.outputs[key])) {
        return point.outputs[key];
    }
    return undefined;
}

// Merge helpers for indicator series
function mergePointPreferDefined(a, b) {
    // both have same time, choose one with defined numeric value; if both defined, prefer b (incoming)
    const av = (a && typeof a.value === 'number' && Number.isFinite(a.value)) ? a.value : undefined;
    const bv = (b && typeof b.value === 'number' && Number.isFinite(b.value)) ? b.value : undefined;
    if (bv !== undefined) return { time: b.time, value: b.value };
    if (av !== undefined) return { time: a.time, value: a.value };
    return { time: a.time }; // whitespace
}

function mergeSeriesArray(existing, incoming) {
    const ex = Array.isArray(existing) ? existing : [];
    const inc = Array.isArray(incoming) ? incoming : [];
    if (ex.length === 0) return inc.length === 0 ? ex : inc.slice();
    if (inc.length === 0) return ex;
    // Both sorted by time asc; merge like merge-sort with dedup by time
    const out = [];
    let i = 0, j = 0;
    while (i < ex.length && j < inc.length) {
        const a = ex[i], b = inc[j];
        if (a.time < b.time) { out.push(a); i++; }
        else if (a.time > b.time) { out.push(b); j++; }
        else { out.push(mergePointPreferDefined(a, b)); i++; j++; }
    }
    while (i < ex.length) out.push(ex[i++]);
    while (j < inc.length) out.push(inc[j++]);
    const unchanged = out.length === ex.length && out.every((point, index) => {
        const previous = ex[index];
        const keys = new Set([...Object.keys(point || {}), ...Object.keys(previous || {})]);
        return [...keys].every((key) => Object.is(point?.[key], previous?.[key]));
    });
    return unchanged ? ex : out;
}

function mergeSeriesObject(existingObj, incomingObj) {
    const ex = (existingObj && typeof existingObj === 'object') ? existingObj : {};
    const inc = (incomingObj && typeof incomingObj === 'object') ? incomingObj : {};
    const keys = new Set([...Object.keys(ex), ...Object.keys(inc)]);
    const out = {};
    let changed = Object.keys(ex).length !== keys.size;
    keys.forEach((k) => {
        out[k] = mergeSeriesArray(ex[k], inc[k]);
        if (out[k] !== ex[k]) changed = true;
    });
    return changed ? out : ex;
}

function samePrimitiveArray(left, right) {
    if (left === right) return true;
    if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) return false;
    return left.every((value, index) => Object.is(value, right[index]));
}

import { canonicalizeBars, compressGaps, TF_SECONDS } from '../utils/priceUtils';
import { mergeLiveCandleIntoHistory } from '../utils/priceFeedUtils';

const PriceFeedContext = createContext();

export const usePriceFeed = () => useContext(PriceFeedContext);

function resolveEffectiveTimeframe(requestedTimeframe, servedTimeframe) {
    const requested = String(requestedTimeframe || '').toUpperCase();
    const served = String(servedTimeframe || '').toUpperCase();
    return served || requested;
}

const chartHistoryInflight = new Map();
const chartHistoryCache = new Map();
const CHART_HISTORY_CACHE_TTL_MS = 30000;

function buildChartHistoryUrl(params, base = API_BASE_URL) {
    const root = String(base || '').replace(/\/$/, '');
    return `${root}/api/price/chart/history/?${params.toString()}`;
}

async function fetchChartHistoryJson(url, authToken, options = {}) {
    const key = `${url}|${authToken || ''}`;
    const now = Date.now();
    const cached = chartHistoryCache.get(key);
    if (cached && now - cached.ts < CHART_HISTORY_CACHE_TTL_MS) {
        return cached.data;
    }

    // Abortable requests are owned by one chart context. Sharing them would let
    // one rapid switch abort another consumer waiting on the same URL.
    const existing = options.signal ? null : chartHistoryInflight.get(key);
    if (existing) {
        return existing;
    }

    const request = fetch(url, {
        headers: { 'Authorization': `Bearer ${authToken}` },
        signal: options.signal,
    }).then(async (response) => {
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        chartHistoryCache.set(key, { ts: Date.now(), data });
        if (chartHistoryCache.size > 50) {
            chartHistoryCache.delete(chartHistoryCache.keys().next().value);
        }
        return data;
    }).finally(() => {
        if (chartHistoryInflight.get(key) === request) {
            chartHistoryInflight.delete(key);
        }
    });

    if (!options.signal) chartHistoryInflight.set(key, request);
    return request;
}

// Normalize indicators to a lowercase-keyed map: { [nameLower]: meta }
function buildIndicatorKey(name) {
    return String(name || '').toLowerCase();
}
function normalizeAvailableIndicators(raw) {
    if (!raw) return {};
    if (Array.isArray(raw)) {
        const map = {};
        raw.forEach((item) => {
            if (!item || typeof item !== 'object') return;
            const key = buildIndicatorKey(item.name || item.display_name || item.id);
            if (key) {
                map[key] = { ...item, name: item.name || item.display_name || item.id };
            }
        });
        return map;
    }
    if (typeof raw === 'object') {
        // Some APIs return { indicators: { name: meta, ... } }
        const src = raw.indicators && typeof raw.indicators === 'object' ? raw.indicators : raw;
        const map = {};
        Object.keys(src).forEach((k) => {
            const item = src[k];
            if (!item || typeof item !== 'object') return;
            const key = buildIndicatorKey(item.name || item.display_name || k);
            map[key] = { ...item, name: item.name || item.display_name || k };
        });
        return map;
    }
    return {};
}

function materializeIndicatorForContext(indicator, symbol, timeframe) {
    const paneType = indicator?.paneType || indicator?.pane_type;
    return {
        ...indicator,
        id: getIndicatorKey(indicator?.name, indicator?.params || {}, symbol, timeframe, paneType),
        backendUniqueId: null,
        data: Array.isArray(indicator?.data) ? [] : {},
        events: [],
        paneType,
        scaleType: indicator?.scaleType || indicator?.scale_type,
    };
}

function sameIndicatorDefinition(indicator, name, params, paneType) {
    return getStudioIndicatorDefinitionKey(indicator) === getStudioIndicatorDefinitionKey({
        name,
        params: params || {},
        paneType,
    });
}

function remapSpecSubscriptions(source, indicators, timeframe) {
    const entries = source instanceof Map
        ? Array.from(source.entries())
        : (Array.isArray(source) ? source.map((entry) => [entry.uniqueId, entry]) : []);
    const remapped = new Map();

    entries.forEach(([uniqueId, meta]) => {
        if (!uniqueId || !meta?.spec) return;
        const attachToMeta = meta.attachToMeta || {};
        const parent = (Array.isArray(indicators) ? indicators : []).find((indicator) => (
            sameIndicatorDefinition(
                indicator,
                attachToMeta.parentName,
                attachToMeta.parentParams,
                indicator?.paneType,
            )
        ));
        const parentId = parent?.id;
        remapped.set(uniqueId, {
            ...meta,
            timeframe,
            attachToId: parentId,
            attachToMeta: attachToMeta.parentName
                ? { ...attachToMeta, parentId }
                : undefined,
        });
    });
    return remapped;
}

const PriceFeedProvider = ({ children, accountId, symbol, timeframe, persistIndicators = false }) => {
    const persistedIndicatorStateRef = useRef(null);
    if (persistedIndicatorStateRef.current === null) {
        persistedIndicatorStateRef.current = persistIndicators
            ? loadStudioIndicatorState()
            : { indicators: [], specs: [] };
    }
    const [candle, setCandle] = useState(null);
    const [bid, setBid] = useState(null);
    const [ask, setAsk] = useState(null);
    const [historicalData, setHistoricalData] = useState([]);
    const [isConnected, setIsConnected] = useState(false);
    const [historyStatus, setHistoryStatus] = useState('idle');
    const [historyError, setHistoryError] = useState(null);
    const [historyRetryNonce, setHistoryRetryNonce] = useState(() => 0);
    const [isLoggingEnabled, setIsLoggingEnabled] = useState(false);
    const isLoggingEnabledRef = useRef(isLoggingEnabled);
    isLoggingEnabledRef.current = isLoggingEnabled;
    const [activeIndicators, setActiveIndicators] = useState(() => (
        persistedIndicatorStateRef.current.indicators.map((indicator) => (
            materializeIndicatorForContext(indicator, symbol, timeframe)
        ))
    ));
    const activeIndicatorsRef = useRef(activeIndicators);
    // Lightweight live indicator point update (avoids re-render storm from setActiveIndicators on every tick)
    const [indicatorLiveUpdate, setIndicatorLiveUpdate] = useState(null);
    // ---- Throttle high-frequency WS state updates to prevent render storms ----
    const pendingBidRef = useRef(null);
    const pendingAskRef = useRef(null);
    const pendingCandleRef = useRef(null);
    const pendingHistoricalRef = useRef(null); // null = no pending update, otherwise the updater fn or value
    const throttleRafRef = useRef(null);
    const lastFlushRef = useRef(0);
    const THROTTLE_MS = 100; // max flush frequency

    const clearPendingMarketUpdates = useCallback(() => {
        if (throttleRafRef.current) {
            cancelAnimationFrame(throttleRafRef.current);
            clearTimeout(throttleRafRef.current);
        }
        throttleRafRef.current = null;
        pendingBidRef.current = null;
        pendingAskRef.current = null;
        pendingCandleRef.current = null;
        pendingHistoricalRef.current = null;
    }, []);

    const flushPendingUpdates = useCallback(() => {
        throttleRafRef.current = null;
        lastFlushRef.current = Date.now();
        if (pendingBidRef.current !== null) {
            const val = pendingBidRef.current;
            pendingBidRef.current = null;
            setBid(val);
        }
        if (pendingAskRef.current !== null) {
            const val = pendingAskRef.current;
            pendingAskRef.current = null;
            setAsk(val);
        }
        if (pendingCandleRef.current !== null) {
            const val = pendingCandleRef.current;
            pendingCandleRef.current = null;
            setCandle(val);
        }
        if (pendingHistoricalRef.current !== null) {
            const updater = pendingHistoricalRef.current;
            pendingHistoricalRef.current = null;
            setHistoricalData(updater);
        }
    }, []);

    const scheduleFlush = useCallback(() => {
        if (throttleRafRef.current) return; // already scheduled
        const elapsed = Date.now() - lastFlushRef.current;
        if (elapsed >= THROTTLE_MS) {
            // Enough time passed — flush on next animation frame
            throttleRafRef.current = requestAnimationFrame(flushPendingUpdates);
        } else {
            // Too soon — delay flush
            throttleRafRef.current = setTimeout(() => {
                throttleRafRef.current = null;
                requestAnimationFrame(flushPendingUpdates);
            }, THROTTLE_MS - elapsed);
        }
    }, [flushPendingUpdates]);

    // Clean up throttle timer on unmount
    useEffect(() => {
        return clearPendingMarketUpdates;
    }, [clearPendingMarketUpdates]);
    const [availableIndicators, setAvailableIndicators] = useState({});
    const availableIndicatorsRef = useRef({});
    const wsRef = useRef(null);
    // Track opened sockets so provider teardown can close every connection.
    const socketsSetRef = useRef(new Set());
    const connectionGenerationRef = useRef(0);
    const contextKeyRef = useRef('');
    contextKeyRef.current = `${accountId || ''}|${symbol || ''}|${timeframe || ''}`;
    // Reconnect state
    const reconnectTimerRef = useRef(null);
    const reconnectDelayRef = useRef(2000);
    const sentIndicatorIdsRef = useRef(new Map());
    const removedIndicatorIdsRef = useRef(new Set());
    const removedIndicatorKeysRef = useRef(new Set());
    const loadingIndicatorsRef = useRef(false);
    const pendingActionsRef = useRef([]);
    // For incremental history growth requests
    const loadMoreInFlightRef = useRef(false);
    const loadMoreRequestIdRef = useRef(0);
    const lastRequestedCountRef = useRef(0);
    const initialHistoryAbortRef = useRef(null);
    // Gate live updates until initial history is applied for the current context
    const historyReadyRef = useRef(false);
    // Track latest history length synchronously
    const historicalLenRef = useRef(0);
    // Track the actual timeframe served by the backend when fallback data differs
    const effectiveTimeframeRef = useRef(timeframe);
    // Track the last compressed bar {time, time_real} so the WS handler can compute compressed time synchronously
    const lastBarRef = useRef(null);
    const lastLiveCandleRef = useRef(null);
    // Track last indicator hydration length to avoid redundant re-requests
    const lastIndicatorHydrationLenRef = useRef(0);
    // Nested spec (subscribe_spec) state: map of uid -> { data_keys, data }
    const [specStreams, setSpecStreams] = useState({});
    const specStreamsRef = useRef({});
    useEffect(() => { specStreamsRef.current = specStreams; }, [specStreams]);
    const activeSpecSubsRef = useRef(remapSpecSubscriptions(
        persistedIndicatorStateRef.current.specs,
        activeIndicators,
        timeframe,
    )); // uid -> { spec, timeframe, count, attachToId? }
    const indicatorPersistenceEnabledRef = useRef(persistIndicators);
    const lastPersistedIndicatorStateRef = useRef(
        persistIndicators
            ? serializeStudioIndicatorState(activeIndicators, activeSpecSubsRef.current)
            : null,
    );

    const persistDesiredIndicators = useCallback(() => {
        if (!persistIndicators || !indicatorPersistenceEnabledRef.current) return;
        const serialized = serializeStudioIndicatorState(
            activeIndicatorsRef.current,
            activeSpecSubsRef.current,
        );
        if (serialized === lastPersistedIndicatorStateRef.current) return;
        storeStudioIndicatorState(serialized);
        lastPersistedIndicatorStateRef.current = serialized;
    }, [persistIndicators]);

    useEffect(() => {
        effectiveTimeframeRef.current = timeframe;
    }, [timeframe]);

    // Keep refs in sync with state
    const retryHistory = useCallback(() => {
        const params = new URLSearchParams({
            account_id: accountId || '',
            symbol: symbol || '',
            resolution: timeframe || '',
            count: 1000,
        });
        const authToken = localStorage.getItem('token') || '';
        chartHistoryCache.delete(`${buildChartHistoryUrl(params)}|${authToken}`);
        setHistoryError(null);
        setHistoryStatus('loading');
        setHistoryRetryNonce((nonce) => nonce + 1);
    }, [accountId, symbol, timeframe]);
    useEffect(() => {
        activeIndicatorsRef.current = Array.isArray(activeIndicators) ? activeIndicators : [];
        persistDesiredIndicators();
    }, [activeIndicators, persistDesiredIndicators]);

    useEffect(() => {
        indicatorPersistenceEnabledRef.current = persistIndicators;
    }, [persistIndicators]);

    useEffect(() => {
        if (!persistIndicators) return undefined;
        const handleLogout = () => {
            indicatorPersistenceEnabledRef.current = false;
            activeIndicatorsRef.current = [];
            activeSpecSubsRef.current.clear();
            setActiveIndicators([]);
            setSpecStreams({});
        };
        window.addEventListener('paxia:logout', handleLogout);
        return () => window.removeEventListener('paxia:logout', handleLogout);
    }, [persistIndicators]);

    // Keep ref in sync
    useEffect(() => {
        availableIndicatorsRef.current = availableIndicators || {};
    }, [availableIndicators]);

    useEffect(() => {
        const loadInitialData = async () => {
            const token = localStorage.getItem("token");
            if (!token) {
                setAvailableIndicators({});
                availableIndicatorsRef.current = {};
                return;
            }
            try {
                loadingIndicatorsRef.current = true;
                const indicators = await fetchAvailableIndicators();
                const normalized = normalizeAvailableIndicators(indicators);
                setAvailableIndicators(normalized);
                availableIndicatorsRef.current = normalized;
            } catch (error) {
                console.error("Failed to load available indicators:", error);
            } finally {
                loadingIndicatorsRef.current = false;
            }
        };
        loadInitialData();
    }, []);

    const ensureIndicatorsLoaded = useCallback(async () => {
        if (availableIndicatorsRef.current && Object.keys(availableIndicatorsRef.current).length > 0) return;
        if (loadingIndicatorsRef.current) {
            await new Promise((resolve) => {
                const start = Date.now();
                const check = () => {
                    if (!loadingIndicatorsRef.current || (availableIndicatorsRef.current && Object.keys(availableIndicatorsRef.current).length > 0)) return resolve();
                    if (Date.now() - start > 5000) return resolve();
                    setTimeout(check, 50);
                };
                check();
            });
            return;
        }
        try {
            loadingIndicatorsRef.current = true;
            const indicators = await fetchAvailableIndicators();
            const normalized = normalizeAvailableIndicators(indicators);
            setAvailableIndicators(normalized);
            availableIndicatorsRef.current = normalized;
        } catch (e) {
            console.error('Failed to fetch available indicators on demand:', e);
        } finally {
            loadingIndicatorsRef.current = false;
        }
    }, []);

    const sendIndicatorAction = useCallback((action, payload) => {
        const socket = wsRef.current;
        const message = { action, ...payload };
        if (action === 'remove_indicator' && payload?.unique_id != null) {
            removedIndicatorIdsRef.current.add(String(payload.unique_id));
        } else if (action === 'add_indicator' && payload?.unique_id != null) {
            removedIndicatorIdsRef.current.delete(String(payload.unique_id));
            removedIndicatorKeysRef.current.delete(String(payload.unique_id));
        }
        if (socket && socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify(message));
            if (isLoggingEnabledRef.current) {
                console.log(`Sent indicator action '${action}':`, payload);
            }
        } else {
            pendingActionsRef.current.push(message);
            if (isLoggingEnabledRef.current) {
                console.log(`Queued indicator action '${action}' (socket not open yet).`);
            }
        }
    }, []);

    const removeActiveIndicator = useCallback((indicatorId) => {
        if (!indicatorId) return false;
        const target = activeIndicatorsRef.current.find((indicator) => indicator.id === indicatorId);
        if (!target) return false;

        removedIndicatorKeysRef.current.add(String(indicatorId));
        const remaining = activeIndicatorsRef.current.filter((indicator) => indicator.id !== indicatorId);
        activeIndicatorsRef.current = remaining;
        setActiveIndicators(remaining);
        persistDesiredIndicators();

        if (target.backendUniqueId != null) {
            sendIndicatorAction('remove_indicator', { unique_id: target.backendUniqueId });
        }
        return true;
    }, [persistDesiredIndicators, sendIndicatorAction]);

    useEffect(() => {
        const generation = connectionGenerationRef.current + 1;
        connectionGenerationRef.current = generation;
        const isCurrent = () => connectionGenerationRef.current === generation;

        const retainedIndicators = activeIndicatorsRef.current.map((indicator) => (
            materializeIndicatorForContext(indicator, symbol, timeframe)
        ));
        activeIndicatorsRef.current = retainedIndicators;
        activeSpecSubsRef.current = remapSpecSubscriptions(
            activeSpecSubsRef.current,
            retainedIndicators,
            timeframe,
        );
        setActiveIndicators(retainedIndicators);
        persistDesiredIndicators();

        const closeSocket = (socket) => {
            if (!socket) return;
            try {
                if (socket.readyState === WebSocket.OPEN) {
                    socket.send(JSON.stringify({ action: 'unsubscribe' }));
                }
            } catch { /* ignore */ }
            try { socket.close(); } catch { /* ignore */ }
            socketsSetRef.current.delete(socket);
        };

        const previousSocket = wsRef.current;
        wsRef.current = null;
        closeSocket(previousSocket);
        clearPendingMarketUpdates();
        historyReadyRef.current = false;
        lastBarRef.current = null;
        lastLiveCandleRef.current = null;
        historicalLenRef.current = 0;
        lastRequestedCountRef.current = 0;
        loadMoreRequestIdRef.current += 1;
        loadMoreInFlightRef.current = false;
        setCandle(null);
        setBid(null);
        setAsk(null);
        setHistoricalData([]);
        setSpecStreams({});
        setHistoryError(null);
        setHistoryStatus(
            accountId && symbol && timeframe ? 'loading' : 'idle',
        );
        setIsConnected(false);
        sentIndicatorIdsRef.current.clear();
        removedIndicatorIdsRef.current.clear();
        removedIndicatorKeysRef.current.clear();
        pendingActionsRef.current = [];

        const connectAndSubscribe = async () => {
            if (!accountId || !symbol || !timeframe) {
                return;
            }

            await ensureIndicatorsLoaded();
            if (!isCurrent()) return;

            const authToken = localStorage.getItem("token");
            if (!authToken) {
                console.error("Authentication token not found.");
                setHistoryStatus('error');
                setHistoryError('Sign in again to load chart history.');
                return;
            }

            // 3. Fetch historical data FIRST (initial batch of 1000 for fast render)
            const params = new URLSearchParams({
                account_id: accountId,
                symbol: symbol,
                resolution: timeframe,
                count: 1000,
            });
            try {
                // Block live updates until history is in place
                historyReadyRef.current = false;
                setHistoryStatus('loading');
                setHistoryError(null);
                initialHistoryAbortRef.current?.abort();
                const historyController = new AbortController();
                initialHistoryAbortRef.current = historyController;
                const responseData = await fetchChartHistoryJson(
                    buildChartHistoryUrl(params),
                    authToken,
                    { signal: historyController.signal },
                );
                if (!isCurrent() || historyController.signal.aborted || initialHistoryAbortRef.current !== historyController) {
                    return;
                }
                initialHistoryAbortRef.current = null;
                // Detect if server fell back to a different timeframe (e.g. D1 when M30 was requested)
                if (responseData.served_timeframe && responseData.served_timeframe !== timeframe) {
                    console.warn(`Price service served ${responseData.served_timeframe} instead of requested ${timeframe}`);
                }
                effectiveTimeframeRef.current = resolveEffectiveTimeframe(timeframe, responseData.served_timeframe);
                const sourceCandleCount = Array.isArray(responseData.candles) ? responseData.candles.length : 0;
                const formattedData = canonicalizeBars((responseData.candles || [])
                    .map(normalizeHistoryCandle)
                    .filter(Boolean));
                const compressed = compressGaps(formattedData, effectiveTimeframeRef.current);
                setHistoricalData(compressed);
                historicalLenRef.current = compressed.length;
                if (compressed.length > 0) {
                    const lb = compressed[compressed.length - 1];
                    lastBarRef.current = { time: lb.time, time_real: lb.time_real ?? lb.time };
                    lastLiveCandleRef.current = lb;
                }
                if (compressed.length === 0) {
                    const reason = responseData?.transient_error
                        ? 'Chart history is temporarily unavailable. Try again.'
                        : sourceCandleCount === 0
                            ? `No ${timeframe} chart history is available for ${symbol}.`
                            : 'The received chart history did not contain valid OHLC values.';
                    console.warn('Chart history contained no renderable candles.', {
                        accountId,
                        symbol,
                        timeframe,
                        sourceCandleCount,
                        transientError: Boolean(responseData?.transient_error),
                    });
                    setHistoryStatus('empty');
                    setHistoryError(reason);
                } else {
                    setHistoryStatus('ready');
                    setHistoryError(null);
                }
                lastRequestedCountRef.current = formattedData.length;
                // Allow a tick so consumers (e.g., chart) can ingest history before live updates arrive
                setTimeout(() => {
                    if (isCurrent()) historyReadyRef.current = historicalLenRef.current > 0;
                }, 0);
            } catch (error) {
                if (!isCurrent() || error?.name === 'AbortError') {
                    return;
                }
                setHistoryStatus('error');
                setHistoryError('Unable to load chart history. Try again.');
                console.error('Error fetching historical data:', error);
                const delay = reconnectDelayRef.current;
                clearTimeout(reconnectTimerRef.current);
                reconnectTimerRef.current = setTimeout(() => {
                    if (isCurrent()) connectAndSubscribe();
                }, delay);
                reconnectDelayRef.current = Math.min(delay * 1.5, 30000);
                return;
            }

            if (!isCurrent()) return;

            // 4. NOW, establish new WebSocket connection
            const origin = WS_BASE_URL || (() => {
                const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
                return `${proto}://${window.location.host}`;
            })();
            const wsUrl = `${origin}/ws/prices/${accountId}/${symbol}/?token=${authToken}`;
            closeSocket(wsRef.current);
            const ws = new WebSocket(wsUrl);
            wsRef.current = ws;
            socketsSetRef.current.add(ws);

            ws.onopen = () => {
                if (!isCurrent() || wsRef.current !== ws) {
                    closeSocket(ws);
                    return;
                }
                setIsConnected(true);
                reconnectDelayRef.current = 2000; // reset backoff on successful connect
                console.log(`Price socket connected for ${symbol}@${timeframe}.`);

                const subscriptionMessage = { action: 'subscribe', timeframe };
                if (isLoggingEnabledRef.current) {
                    console.log(`Attempting to subscribe to ${symbol} ${timeframe} candles.`, {
                        url: wsUrl,
                        message: subscriptionMessage,
                    });
                }
                ws.send(JSON.stringify(subscriptionMessage));
                if (isLoggingEnabledRef.current) {
                    console.log(`Subscribing to live price and ${timeframe} candle updates for ${symbol}.`);
                }

                // Re-add any existing indicators
                activeIndicatorsRef.current.forEach(indicator => {
                    const key = getIndicatorKey(indicator.name, indicator.params, symbol, timeframe, indicator.paneType);
                    const msg = {
                        action: 'add_indicator',
                        indicator: indicator.name,
                        params: indicator.params,
                        unique_id: key,
                        count: 2700,
                        pane_type: indicator.paneType,
                        scale_type: indicator.scaleType
                    };
                    ws.send(JSON.stringify(msg));
                });

                // Flush any buffered actions queued before the socket was open
                if (pendingActionsRef.current.length > 0) {
                    for (const msg of pendingActionsRef.current) {
                        ws.send(JSON.stringify(msg));
                    }
                    pendingActionsRef.current = [];
                }

                // Re-subscribe any active nested spec subscriptions (those weren't queued to avoid duplicates)
                try {
                    activeSpecSubsRef.current.forEach((sub, uid) => {
                        const payload = {
                            type: 'subscribe_spec',
                            unique_id: uid,
                            spec: sub.spec,
                            timeframe: sub.timeframe || timeframe,
                            count: sub.count || 500,
                        };
                        ws.send(JSON.stringify(payload));
                        if (isLoggingEnabledRef.current) console.log('Re-subscribed spec', payload);
                    });
                } catch (e) {
                    console.warn('Failed to re-subscribe specs:', e);
                }
            };

            ws.onclose = (evt) => {
                socketsSetRef.current.delete(ws);
                if (!isCurrent() || wsRef.current !== ws) return;
                wsRef.current = null;
                setIsConnected(false);
                // Auto-reconnect if this is still the active socket
                if (accountId && symbol && timeframe) {
                    const delay = reconnectDelayRef.current;
                    console.log(`Price socket disconnected (code=${evt?.code}). Reconnecting in ${delay}ms…`);
                    clearTimeout(reconnectTimerRef.current);
                    reconnectTimerRef.current = setTimeout(() => {
                        connectAndSubscribe();
                    }, delay);
                    reconnectDelayRef.current = Math.min(delay * 1.5, 30000);
                } else {
                    console.log('Price socket disconnected (stale, not reconnecting).');
                }
            };
            ws.onerror = () => {
                if (isCurrent() && wsRef.current === ws) setIsConnected(false);
            };

            ws.onmessage = (event) => {
                // Ignore messages from stale sockets
                if (!isCurrent() || wsRef.current !== ws) return;
                try {
                    const message = JSON.parse(event.data.replace(/NaN/g, 'null'));
                    if (isLoggingEnabledRef.current) console.log('WS Message:', message);

                    if (message.type === 'new_candle') {
                        // Until initial history is applied, ignore early live candles to avoid visual overlap
                        if (!historyReadyRef.current) {
                            if (isLoggingEnabledRef.current) console.log('Dropped new_candle before history ready');
                            return;
                        }
                        const msgAcct = (message.account_id || message.data?.account_id);
                        // Account mismatch can happen if backend uses a different identifier; don't drop, just log.
                        if (msgAcct && String(msgAcct) !== String(accountId)) {
                            if (isLoggingEnabledRef.current) console.warn('Account mismatch on new_candle (proceeding):', { msgAcct, expected: accountId });
                        }
                        const msgSym = (message.symbol || message.data?.symbol || message.instrument || message.data?.instrument);
                        if (msgSym && String(msgSym).toUpperCase() !== String(symbol).toUpperCase()) {
                            if (isLoggingEnabledRef.current) console.log('Dropped new_candle for other symbol:', msgSym);
                            return;
                        }
                        const nc = normalizeHistoryCandle(message.data);
                        if (nc && Number.isFinite(nc.time)) {
                            // Compute compressed time synchronously from lastBarRef
                            const last = lastBarRef.current;
                            if (last && Number.isFinite(last.time_real) && nc.time < last.time_real) return;
                            const interval = TF_SECONDS[(effectiveTimeframeRef.current || timeframe || '').toUpperCase()] || 300;
                            const gapThreshold = interval * 3;
                            let compressedTime = nc.time;
                            if (last && typeof last.time_real === 'number') {
                                if (last.time_real === nc.time) {
                                    // Same bar update — keep existing compressed time
                                    compressedTime = last.time;
                                } else if (nc.time > last.time_real) {
                                    // New bar — compress gap if needed
                                    const gap = nc.time - last.time_real;
                                    compressedTime = gap > gapThreshold ? last.time + interval : last.time + gap;
                                }
                            }
                            const d = message?.data;
                            const vol = Number.isFinite(d?.volume) ? Number(d.volume) : (Number.isFinite(d?.tick_volume) ? Number(d.tick_volume) : (Number.isFinite(d?.tickVolume) ? Number(d.tickVolume) : undefined));
                            const tVol = Number.isFinite(d?.tick_volume) ? Number(d.tick_volume) : (Number.isFinite(d?.tickVolume) ? Number(d.tickVolume) : undefined);
                            const nextLiveBar = {
                                ...nc,
                                time: compressedTime,
                                time_real: nc.time,
                                feedContextKey: `${accountId || ''}|${symbol || ''}|${timeframe || ''}`,
                                volume: vol,
                                tickVolume: tVol,
                            };
                            const previousLiveBar = lastLiveCandleRef.current;
                            const isNewBar = !last || nc.time > last.time_real;

                            // Update lastBarRef synchronously so next WS message sees the correct state
                            lastBarRef.current = { time: compressedTime, time_real: nc.time };
                            lastLiveCandleRef.current = nextLiveBar;

                            // Buffer candle update (throttled to prevent render storms)
                            pendingCandleRef.current = nextLiveBar;

                            // Keep bulk history stable during same-bar ticks. The chart consumes
                            // those through series.update(); history only changes on a new bar.
                            if (isNewBar) {
                                const pendingHistory = pendingHistoricalRef.current;
                                pendingHistoricalRef.current = (prev) => {
                                    const base = typeof pendingHistory === 'function' ? pendingHistory(prev) : prev;
                                    const next = mergeLiveCandleIntoHistory(base, nextLiveBar, previousLiveBar);
                                    historicalLenRef.current = next.length;
                                    return next;
                                };
                            }
                            scheduleFlush();
                        }
                    } else if (message.type === 'live_price') {
                        const msgAcct = (message.account_id || message.data?.account_id);
                        // Account mismatch can happen if backend uses a different identifier; don't drop, just log.
                        if (msgAcct && String(msgAcct) !== String(accountId)) {
                            if (isLoggingEnabledRef.current) console.warn('Account mismatch on live_price (proceeding):', { msgAcct, expected: accountId });
                        }
                        const msgSym = (message.symbol || message.data?.symbol || message.instrument || message.data?.instrument);
                        if (msgSym && String(msgSym).toUpperCase() !== String(symbol).toUpperCase()) {
                            if (isLoggingEnabledRef.current) console.log('Dropped live_price for other symbol:', msgSym);
                            return;
                        }
                        const nextBid = message?.data?.bid;
                        const nextAsk = message?.data?.ask;
                        // Buffer bid/ask updates (throttled to prevent render storms)
                        if (nextBid != null) pendingBidRef.current = nextBid;
                        if (nextAsk != null) pendingAskRef.current = nextAsk;
                        if (nextBid != null || nextAsk != null) scheduleFlush();
                    } else if (message.type === 'indicator_data') {
                        const msgAcct = (message.account_id || message.data?.account_id);
                        // Account mismatch can happen if backend uses a different identifier; don't drop, just log.
                        if (msgAcct && String(msgAcct) !== String(accountId)) {
                            if (isLoggingEnabledRef.current) console.warn('Account mismatch on indicator_data (proceeding):', { msgAcct, expected: accountId });
                        }
                        const msgSym = (message.symbol || message.data?.symbol || message.instrument || message.data?.instrument);
                        if (msgSym && String(msgSym).toUpperCase() !== String(symbol).toUpperCase()) {
                            if (isLoggingEnabledRef.current) console.log('Dropped indicator_data for other symbol:', msgSym);
                            return;
                        }
                        const {
                            indicator,
                            data,
                            events,
                            unique_id: backendId,
                            params,
                            data_keys,
                        } = message;
                        if (backendId != null && removedIndicatorIdsRef.current.has(String(backendId))) {
                            if (isLoggingEnabledRef.current) {
                                console.log('Dropped indicator_data for an indicator pending removal:', backendId);
                            }
                            return;
                        }
                        const normalizedEvents = normalizeIndicatorEvents(events);
                        const infoKey = buildIndicatorKey(indicator);
                        let indicatorInfo = availableIndicatorsRef.current[infoKey];
                        if (!indicatorInfo) {
                            // In the unlikely event metadata still isn't ready, try once more lazily
                            if (isLoggingEnabledRef.current) console.warn('Indicator metadata missing; attempting lazy load.');
                            ensureIndicatorsLoaded().then(() => {
                                const refreshed = availableIndicatorsRef.current[infoKey];
                                if (!refreshed) return;
                                const stableKey = getIndicatorKey(indicator, params, symbol, timeframe, refreshed.pane_type);
                                if (removedIndicatorKeysRef.current.has(String(stableKey))) {
                                    if (backendId != null) {
                                        removedIndicatorIdsRef.current.add(String(backendId));
                                        if (ws.readyState === WebSocket.OPEN) {
                                            ws.send(JSON.stringify({ action: 'remove_indicator', unique_id: backendId }));
                                        }
                                    }
                                    return;
                                }
                                const normalizedData = normalizeIndicatorPayload(data);
                                setActiveIndicators(prev => {
                                    const newIndicators = [...prev];
                                    const idx = newIndicators.findIndex((item) => (
                                        item.id === stableKey
                                        || sameIndicatorDefinition(item, indicator, params, refreshed.pane_type)
                                    ));
                                    if (idx !== -1) {
                                        newIndicators[idx] = {
                                            ...newIndicators[idx],
                                            data: normalizedData,
                                            backendUniqueId: backendId,
                                            data_keys,
                                            indicatorMeta: refreshed,
                                            events: mergeIndicatorEvents(newIndicators[idx].events, normalizedEvents),
                                        };
                                    } else {
                                        newIndicators.push({
                                            id: stableKey,
                                            backendUniqueId: backendId,
                                            name: indicator,
                                            data: normalizedData,
                                            paneType: refreshed.pane_type,
                                            scaleType: refreshed.scale_type,
                                            params,
                                            data_keys,
                                            indicatorMeta: refreshed,
                                            events: mergeIndicatorEvents([], normalizedEvents),
                                        });
                                    }
                                    sentIndicatorIdsRef.current.set(backendId, stableKey);
                                    // Sync ref immediately so indicator_update handler can find it
                                    activeIndicatorsRef.current = newIndicators;
                                    return newIndicators;
                                });
                            });
                            return;
                        }

                        const stableKey = getIndicatorKey(indicator, params, symbol, timeframe, indicatorInfo.pane_type);
                        if (removedIndicatorKeysRef.current.has(String(stableKey))) {
                            if (backendId != null) {
                                removedIndicatorIdsRef.current.add(String(backendId));
                                if (ws.readyState === WebSocket.OPEN) {
                                    ws.send(JSON.stringify({ action: 'remove_indicator', unique_id: backendId }));
                                }
                            }
                            return;
                        }
                        const normalizedData = normalizeIndicatorPayload(data);
                        
                        setActiveIndicators(prev => {
                            const newIndicators = [...prev];
                            const idx = newIndicators.findIndex((item) => (
                                item.id === stableKey
                                || sameIndicatorDefinition(item, indicator, params, indicatorInfo.pane_type)
                            ));
                            if (idx !== -1) {
                                const current = newIndicators[idx];
                                const merged = Array.isArray(normalizedData)
                                    ? mergeSeriesArray(current.data, normalizedData)
                                    : mergeSeriesObject(current.data, normalizedData);
                                const mergedEvents = mergeIndicatorEvents(current.events, normalizedEvents);
                                if (
                                    merged === current.data
                                    && mergedEvents === current.events
                                    && Object.is(current.backendUniqueId, backendId)
                                    && samePrimitiveArray(current.data_keys, data_keys)
                                    && current.indicatorMeta === indicatorInfo
                                ) {
                                    return prev;
                                }
                                newIndicators[idx] = {
                                    ...current,
                                    data: merged,
                                    backendUniqueId: backendId,
                                    data_keys,
                                    indicatorMeta: indicatorInfo,
                                    events: mergedEvents,
                                };
                            } else {
                                newIndicators.push({
                                    id: stableKey,
                                    backendUniqueId: backendId,
                                    name: indicator,
                                    data: normalizedData,
                                    paneType: indicatorInfo.pane_type,
                                    scaleType: indicatorInfo.scale_type,
                                    params,
                                    data_keys,
                                    indicatorMeta: indicatorInfo,
                                    events: mergeIndicatorEvents([], normalizedEvents),
                                });
                            }
                            sentIndicatorIdsRef.current.set(backendId, stableKey);
                            // Sync ref immediately so indicator_update handler can find it
                            activeIndicatorsRef.current = newIndicators;
                            return newIndicators;
                        });
                    } else if (message.type === 'indicator_event') {
                        const backendId = message.unique_id;
                        const frontendId = sentIndicatorIdsRef.current.get(backendId);
                        const incomingEvents = normalizeIndicatorEvents(getIndicatorEventPayload(message));
                        if (incomingEvents.length === 0) return;

                        setActiveIndicators((prev) => {
                            let changed = false;
                            const next = prev.map((indicator) => {
                                const matchesFrontendId = frontendId && indicator.id === frontendId;
                                const matchesBackendId = backendId != null
                                    && String(indicator.backendUniqueId) === String(backendId);
                                if (!matchesFrontendId && !matchesBackendId) return indicator;

                                const mergedEvents = mergeIndicatorEvents(indicator.events, incomingEvents);
                                if (mergedEvents === indicator.events) return indicator;
                                changed = true;
                                return { ...indicator, events: mergedEvents };
                            });
                            if (!changed) return prev;
                            activeIndicatorsRef.current = next;
                            return next;
                        });
                    } else if (message.type === 'indicator_removed') {
                        const { unique_id: backendId } = message;
                        const frontendId = sentIndicatorIdsRef.current.get(backendId);
                        if (frontendId) {
                            setActiveIndicators(prev => prev.filter(ind => ind.id !== frontendId));
                            // Also remove any nested spec streams attached to this indicator
                            try {
                                const streams = Object.entries(specStreamsRef.current || {});
                                streams.forEach(([uid, entry]) => {
                                    const pid = entry?.attachToId || entry?.attachToMeta?.parentId;
                                    const byMeta = entry?.attachToMeta ? (String(entry.attachToMeta.parentName||'').toLowerCase() === String(message.indicator||'').toLowerCase()) : false;
                                    if (pid === frontendId || byMeta) {
                                        // Remove from state and unsubscribe
                                        setSpecStreams(prev => {
                                            const rest = { ...(prev || {}) };
                                            delete rest[uid];
                                            return rest;
                                        });
                                        activeSpecSubsRef.current.delete(uid);
                                        try {
                                            const socket = wsRef.current;
                                            const payload = { type: 'unsubscribe_spec', unique_id: uid };
                                            if (socket && socket.readyState === WebSocket.OPEN) socket.send(JSON.stringify(payload));
                                        } catch (error) {
                                            if (isLoggingEnabledRef.current) console.debug('Failed to unsubscribe spec socket:', error);
                                        }
                                    }
                                });
                            } catch (error) {
                                if (isLoggingEnabledRef.current) console.debug('Failed to clean nested spec streams:', error);
                            }
                        }
                        sentIndicatorIdsRef.current.delete(backendId);
                        if (backendId != null) removedIndicatorIdsRef.current.delete(String(backendId));
                    } else if (message.type === 'indicator_update') {
                        // Optionally gate indicator updates as well until history is ready
                        if (!historyReadyRef.current) {
                            if (isLoggingEnabledRef.current) console.log('Dropped indicator_update before history ready');
                            return;
                        }
                        const msgAcct = (message.account_id || message.data?.account_id);
                        // Account mismatch can happen if backend uses a different identifier; don't drop, just log.
                        if (msgAcct && String(msgAcct) !== String(accountId)) {
                            if (isLoggingEnabledRef.current) console.warn('Account mismatch on indicator_update (proceeding):', { msgAcct, expected: accountId });
                        }
                        const msgSym = (message.symbol || message.data?.symbol || message.instrument || message.data?.instrument);
                        if (msgSym && String(msgSym).toUpperCase() !== String(symbol).toUpperCase()) {
                            if (isLoggingEnabledRef.current) console.log('Dropped indicator_update for other symbol:', msgSym);
                            return;
                        }
                        if (isLoggingEnabledRef.current) {
                            console.log('Indicator Update Received:', message);
                        }
                        const { unique_id: backendId, data: point, data_keys } = message;
                        const newPoint = { ...point, time: toSeconds(point?.time) };
                        const frontendId = sentIndicatorIdsRef.current.get(backendId);
                        if (frontendId && typeof newPoint.time === 'number') {
                            // Keep live indicator points on the chart's gap-compressed
                            // candle axis, even before React renders the new candle.
                            const lastChartBar = lastBarRef.current;
                            let chartTime = newPoint.time;
                            if (lastChartBar && Number.isFinite(lastChartBar.time) && Number.isFinite(lastChartBar.time_real)) {
                                const interval = TF_SECONDS[(effectiveTimeframeRef.current || timeframe || '').toUpperCase()] || 300;
                                if (newPoint.time === lastChartBar.time_real) {
                                    chartTime = lastChartBar.time;
                                } else if (newPoint.time > lastChartBar.time_real) {
                                    const gap = newPoint.time - lastChartBar.time_real;
                                    chartTime = gap > interval * 3
                                        ? lastChartBar.time + interval
                                        : lastChartBar.time + gap;
                                } else {
                                    chartTime = null;
                                }
                            }
                            if (!Number.isFinite(chartTime)) return;
                            // Helper to derive value for a given key from message.data
                            const valueFor = (k) => {
                                if (!k) return undefined;
                                if (Object.prototype.hasOwnProperty.call(newPoint, k)) return newPoint[k];
                                if (newPoint.value && typeof newPoint.value === 'object' && Object.prototype.hasOwnProperty.call(newPoint.value, k)) return newPoint.value[k];
                                if (newPoint.outputs && typeof newPoint.outputs === 'object' && Object.prototype.hasOwnProperty.call(newPoint.outputs, k)) return newPoint.outputs[k];
                                return undefined;
                            };

                            // Mutate the ref's data in-place (no React re-render for ordinary ticks).
                            // Sparse outputs may appear for the first time after hydration, in
                            // which case the indicator must be promoted to an object-of-arrays.
                            const ind = activeIndicatorsRef.current.find(i => i.id === frontendId);
                            let didAddDataKey = false;
                            if (ind) {
                                const declaredKeys = Array.isArray(ind.data_keys) ? ind.data_keys : [];
                                const storedKeys = ind.data && typeof ind.data === 'object' && !Array.isArray(ind.data)
                                    ? Object.keys(ind.data)
                                    : [];
                                const incomingKeys = Array.isArray(data_keys) ? data_keys : [];
                                const knownKeys = [...new Set([...declaredKeys, ...storedKeys])];
                                const candidateKeys = incomingKeys.length > 0 ? incomingKeys : knownKeys;
                                const finiteIncomingKeys = candidateKeys.filter((key) => Number.isFinite(valueFor(key)));
                                const mergedKeys = [...new Set([...knownKeys, ...finiteIncomingKeys])];
                                const shouldUseObjectData = (
                                    ind.data && typeof ind.data === 'object' && !Array.isArray(ind.data)
                                ) || mergedKeys.length > 1;

                                if (shouldUseObjectData) {
                                    if (Array.isArray(ind.data)) {
                                        const primaryKey = knownKeys[0] || incomingKeys[0] || 'value';
                                        ind.data = {
                                            [primaryKey]: ind.data
                                                .map((existingPoint) => {
                                                    const value = pointValueForKey(existingPoint, primaryKey);
                                                    return Number.isFinite(value)
                                                        ? { time: existingPoint.time, value }
                                                        : null;
                                                })
                                                .filter(Boolean),
                                        };
                                    } else if (!ind.data || typeof ind.data !== 'object') {
                                        ind.data = {};
                                    }

                                    finiteIncomingKeys.forEach((key) => {
                                        if (!Array.isArray(ind.data[key])) {
                                            ind.data[key] = [];
                                            didAddDataKey = true;
                                        }
                                        const v = valueFor(key);
                                        const series = ind.data[key];
                                        const last = series[series.length - 1];
                                        if (last && last.time === newPoint.time) {
                                            series[series.length - 1] = { time: newPoint.time, value: v };
                                        } else {
                                            series.push({ time: newPoint.time, value: v });
                                        }
                                    });
                                } else {
                                    // Array-of-points (single series) case — mutate in place
                                    const dataArr = Array.isArray(ind.data) ? ind.data : [];
                                    const primaryKey = mergedKeys[0] || incomingKeys[0];
                                    let value = undefined;
                                    if (primaryKey) value = valueFor(primaryKey);
                                    if (value == null && Number.isFinite(newPoint.value)) value = newPoint.value;
                                    if (value == null) {
                                        for (const k of Object.keys(newPoint)) {
                                            if (k === 'time') continue;
                                            const val = newPoint[k];
                                            if (Number.isFinite(val)) { value = val; break; }
                                        }
                                    }
                                    if (Number.isFinite(value)) {
                                        const last = dataArr[dataArr.length - 1];
                                        if (last && last.time === newPoint.time) {
                                            dataArr[dataArr.length - 1] = { time: newPoint.time, value };
                                        } else {
                                            dataArr.push({ time: newPoint.time, value });
                                        }
                                    }
                                    ind.data = dataArr;
                                }

                                if (mergedKeys.length > 0) {
                                    didAddDataKey = didAddDataKey
                                        || mergedKeys.some((key) => !declaredKeys.includes(key));
                                    ind.data_keys = mergedKeys;
                                }
                            }

                            // Existing series consume every point through indicatorLiveUpdate.
                            // Publish full indicator state only when a new sparse output must
                            // be materialized; cloning on every new bar causes render churn.
                            if (didAddDataKey) {
                                const synced = activeIndicatorsRef.current.map(i => ({
                                    ...i,
                                    data: Array.isArray(i.data) ? [...i.data]
                                        : (i.data && typeof i.data === 'object')
                                            ? Object.fromEntries(Object.entries(i.data).map(([k, v]) => [k, Array.isArray(v) ? [...v] : v]))
                                            : i.data,
                                }));
                                activeIndicatorsRef.current = synced;
                                setActiveIndicators(synced);
                            }

                            // Emit lightweight live update for TradingChart to handle via series.update()
                            setIndicatorLiveUpdate({
                                id: frontendId,
                                point: newPoint,
                                chart_time: chartTime,
                                data_keys: ind?.data_keys || data_keys,
                                ts: Date.now(),
                            });
                        }
                        } else if (message.type === 'spec_data') {
                            const { unique_id: uid, data, data_keys } = message;
                            // Ignore late/rogue data for specs we are not tracking anymore
                            if (!activeSpecSubsRef.current.has(uid)) {
                                if (isLoggingEnabledRef.current) console.warn('Dropping spec_data for unknown uid', uid);
                                return;
                            }
                            // Normalize times
                            const normalized = Array.isArray(data)
                                ? data.map(p => ({ ...p, time: toSeconds(p.time) }))
                                : (data && typeof data === 'object')
                                    ? Object.fromEntries(Object.entries(data).map(([k, arr]) => [k, (Array.isArray(arr) ? arr.map(p => ({ ...p, time: toSeconds(p.time) })) : arr)]))
                                    : data;
                            setSpecStreams(prev => {
                                const meta = activeSpecSubsRef.current.get(uid) || {};
                                return {
                                    ...prev,
                                    [uid]: { ...(prev[uid] || {}), attachToId: meta.attachToId, attachToMeta: meta.attachToMeta, data_keys: Array.isArray(data_keys) ? data_keys : ['value'], data: normalized },
                                };
                            });
                        } else if (message.type === 'spec_update') {
                            // Optionally gate spec updates until history is ready
                            if (!historyReadyRef.current) {
                                if (isLoggingEnabledRef.current) console.log('Dropped spec_update before history ready');
                                return;
                            }
                            const { unique_id: uid, data: point, data_keys } = message;
                            const t = toSeconds(point?.time);
                            if (typeof t !== 'number') return;
                            setSpecStreams(prev => {
                                const entry = prev[uid];
                                if (!entry) return prev; // wait for seed data
                                const keys = Array.isArray(data_keys) && data_keys.length > 0 ? data_keys : (Array.isArray(entry.data_keys) && entry.data_keys.length > 0 ? entry.data_keys : ['value']);
                                // Helper to read a value from the update for key k
                                const valueFor = (k) => {
                                    if (!k) return undefined;
                                    if (Object.prototype.hasOwnProperty.call(point, k)) return point[k];
                                    if (point.value && typeof point.value === 'object' && Object.prototype.hasOwnProperty.call(point.value, k)) return point.value[k];
                                    if (point.outputs && typeof point.outputs === 'object' && Object.prototype.hasOwnProperty.call(point.outputs, k)) return point.outputs[k];
                                    if (typeof point.value === 'number') return point.value; // fallback for single series
                                    return undefined;
                                };
                                let nextEntry = entry;
                                if (Array.isArray(entry.data)) {
                                    // Single-series
                                    let v = ('value' in point && Number.isFinite(point.value)) ? point.value : undefined;
                                    if (v == null && keys.length > 0) {
                                        const k0 = valueFor(keys[0]);
                                        if (Number.isFinite(k0)) v = k0;
                                    }
                                    if (!Number.isFinite(v)) return prev;
                                    const arr = entry.data;
                                    const last = arr[arr.length - 1];
                                    const nextPoint = { time: t, value: v };
                                    const nextArr = last && last.time === t ? [...arr.slice(0, -1), nextPoint] : [...arr, nextPoint];
                                    nextEntry = { ...entry, data_keys: keys, data: nextArr };
                                } else if (entry.data && typeof entry.data === 'object') {
                                    // Multi-series
                                    const updated = { ...entry.data };
                                    let changed = false;
                                    keys.forEach((k) => {
                                        const v = valueFor(k);
                                        if (v == null || !Number.isFinite(v)) return;
                                        const series = Array.isArray(updated[k]) ? updated[k] : [];
                                        const last = series[series.length - 1];
                                        if (last && last.time === t) {
                                            updated[k] = [...series.slice(0, -1), { time: t, value: v }];
                                        } else {
                                            updated[k] = [...series, { time: t, value: v }];
                                        }
                                        changed = true;
                                    });
                                    if (!changed) return prev;
                                    nextEntry = { ...entry, data_keys: keys, data: updated };
                                }
                                if (nextEntry === entry) return prev;
                                return { ...prev, [uid]: nextEntry };
                            });
                    } else if (message.error) {
                        console.error('WS Error:', message.error);
                        // If the broker/subscribe failed, suppress auto-reconnect —
                        // reconnecting immediately won't help and just spins the loop.
                        const errStr = String(message.error).toLowerCase();
                        if (errStr.includes('subscribe failed') || errStr.includes('broker') || errStr.includes('mt5') || errStr.includes('connection aborted')) {
                            console.warn('Broker unavailable — pausing WS reconnect (will retry in 60s).');
                            // Detach so onclose doesn't trigger immediate reconnect
                            wsRef.current = null;
                            closeSocket(ws);
                            clearTimeout(reconnectTimerRef.current);
                            // Schedule a single slow retry
                            reconnectTimerRef.current = setTimeout(() => {
                                connectAndSubscribe();
                            }, 60_000);
                        }
                    }
                } catch (error) {
                    console.error("Failed to parse WS message:", error, event.data);
                }
            };
        };

        connectAndSubscribe();

        // Cancel all work owned by this account/symbol/timeframe generation.
        return () => {
            if (connectionGenerationRef.current === generation) {
                connectionGenerationRef.current += 1;
            }
            clearTimeout(reconnectTimerRef.current);
            initialHistoryAbortRef.current?.abort();
            initialHistoryAbortRef.current = null;
            clearPendingMarketUpdates();
            historyReadyRef.current = false;
            const socket = wsRef.current;
            wsRef.current = null;
            closeSocket(socket);
        };
    }, [accountId, symbol, timeframe, historyRetryNonce, ensureIndicatorsLoaded, clearPendingMarketUpdates, scheduleFlush, persistDesiredIndicators]);

    // On provider unmount, browser close, or logout — gracefully close all tracked sockets
    useEffect(() => {
        const closeAllSockets = () => {
            try {
                socketsSetRef.current.forEach((ws) => {
                    try {
                        if (ws && ws.readyState === WebSocket.OPEN) {
                            ws.send(JSON.stringify({ action: 'unsubscribe' }));
                        }
                    } catch (error) {
                        void error;
                    }
                    try {
                        ws && ws.close();
                    } catch (error) {
                        void error;
                    }
                });
            } finally {
                socketsSetRef.current.clear();
                // Prevent onclose from scheduling a stale reconnect
                wsRef.current = null;
                clearTimeout(reconnectTimerRef.current);
            }
        };
        window.addEventListener('beforeunload', closeAllSockets);
        window.addEventListener('paxia:logout', closeAllSockets);
        return () => {
            window.removeEventListener('beforeunload', closeAllSockets);
            window.removeEventListener('paxia:logout', closeAllSockets);
            // Full provider teardown (app unmount) — also clean up
            closeAllSockets();
        };
    }, []);

    const toggleLogging = useCallback(() => setIsLoggingEnabled(prev => !prev), []);

    // Public helpers to manage nested spec subscriptions
    const subscribeSpec = useCallback((uniqueId, spec, opts = {}) => {
        if (!uniqueId || !spec) return;
        const payload = {
            type: 'subscribe_spec',
            unique_id: uniqueId,
            spec,
            timeframe: opts.timeframe || timeframe,
            count: opts.count || 500,
        };
        // Track desired subscription for re-subscribe on reconnect
        activeSpecSubsRef.current.set(uniqueId, { spec, timeframe: payload.timeframe, count: payload.count, attachToId: opts.attachToId, attachToMeta: opts.attachToMeta });
        persistDesiredIndicators();
        if (opts.attachToId || opts.attachToMeta) {
            setSpecStreams(prev => ({ ...prev, [uniqueId]: { ...(prev[uniqueId] || {}), attachToId: opts.attachToId, attachToMeta: opts.attachToMeta } }));
        }
        const socket = wsRef.current;
        if (socket && socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify(payload));
            if (isLoggingEnabledRef.current) console.log('Sent subscribe_spec', payload);
        } else {
            // Do NOT queue subscribe_spec to avoid duplicate subscribes; we'll re-send on ws open from activeSpecSubsRef
            if (isLoggingEnabledRef.current) console.log('WS not open; will re-subscribe_spec on open', { uniqueId });
        }
    }, [timeframe, persistDesiredIndicators]);

    const unsubscribeSpec = useCallback((uniqueId) => {
        if (!uniqueId) return;
        activeSpecSubsRef.current.delete(uniqueId);
        persistDesiredIndicators();
        setSpecStreams(prev => {
            const { [uniqueId]: _, ...rest } = prev || {};
            return rest;
        });
        const payload = { type: 'unsubscribe_spec', unique_id: uniqueId };
        const socket = wsRef.current;
        if (socket && socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify(payload));
            if (isLoggingEnabledRef.current) console.log('Sent unsubscribe_spec', payload);
        } else {
            pendingActionsRef.current.push(payload);
            if (isLoggingEnabledRef.current) console.log('Queued unsubscribe_spec (socket not open)', payload);
        }
    }, [persistDesiredIndicators]);

    // Re-subscribe all nested specs on timeframe change so overlays persist across TF switches
    useEffect(() => {
        const socket = wsRef.current;
        activeSpecSubsRef.current.forEach((meta, uid) => {
            const payload = {
                type: 'subscribe_spec',
                unique_id: uid,
                spec: meta.spec,
                timeframe: timeframe,
                count: meta.count || 500,
            };
            // update stored timeframe for this spec
            activeSpecSubsRef.current.set(uid, { ...meta, timeframe });
            if (socket && socket.readyState === WebSocket.OPEN) {
                try {
                    socket.send(JSON.stringify(payload));
                } catch (error) {
                    void error;
                }
            }
        });
    }, [timeframe]);

    // Load more historical data: prefer cursor-based prepend (before+limit), fallback to count-growth
    const loadMoreHistory = useCallback(async (step = 500) => {
        if (loadMoreInFlightRef.current) return { added: 0, total: historicalData.length };
        if (!accountId || !symbol || !timeframe) return { added: 0, total: historicalData.length };
        const requestContextKey = `${accountId || ''}|${symbol || ''}|${timeframe || ''}`;
        if (contextKeyRef.current !== requestContextKey) return { added: 0, total: historicalData.length };
        const isRequestCurrent = () => contextKeyRef.current === requestContextKey;
        const token = localStorage.getItem('token');
        if (!token) return { added: 0, total: historicalData.length };

        const prev = Array.isArray(historicalData) ? historicalData : [];
        const prevLen = prev.length;
        // Use time_real (original timestamp before gap compression) for cursors if available
        const oldest = prevLen > 0 ? (prev[0]?.time_real ?? prev[0]?.time) : undefined;

        // Try cursor-based prepend first when we have an oldest bar
        if (typeof oldest === 'number' && Number.isFinite(oldest)) {
            const requestId = loadMoreRequestIdRef.current + 1;
            loadMoreRequestIdRef.current = requestId;
            loadMoreInFlightRef.current = true;
            try {
                const params = new URLSearchParams({
                    account_id: accountId,
                    symbol,
                    resolution: timeframe,
                    before: String(oldest),
                    limit: String(step),
                });
                // Use sanitized API_BASE_URL (full origin) exported from config
                const base = API_BASE_URL.replace(/\/$/, '');
                const json = await fetchChartHistoryJson(buildChartHistoryUrl(params, base), token);
                if (!isRequestCurrent() || loadMoreRequestIdRef.current !== requestId) {
                    return { added: 0, total: prevLen };
                }
                effectiveTimeframeRef.current = resolveEffectiveTimeframe(timeframe, json?.served_timeframe || effectiveTimeframeRef.current);
                const raw = Array.isArray(json?.candles) ? json.candles : [];
                const chunk = canonicalizeBars(raw
                    .map(d => ({
                        time: toSeconds(d.time),
                        ...(d.time_real != null ? { time_real: toSeconds(d.time_real) } : {}),
                        open: d.open,
                        high: d.high,
                        low: d.low,
                        close: d.close,
                        volume: (Number.isFinite(d.volume) ? Number(d.volume) : (Number.isFinite(d.tick_volume) ? Number(d.tick_volume) : (Number.isFinite(d.tickVolume) ? Number(d.tickVolume) : undefined))),
                        tickVolume: (Number.isFinite(d.tick_volume) ? Number(d.tick_volume) : (Number.isFinite(d.tickVolume) ? Number(d.tickVolume) : undefined)),
                    }))
                    .filter(d => typeof d.time === 'number' && (d.time_real ?? d.time) < oldest));

                if (chunk.length > 0) {
                    // Restore prev bars to real timestamps for merge, then re-compress everything
                    const prevReal = prev.map((bar) => {
                        const realTime = bar.time_real ?? bar.time;
                        const { time_real: _timeReal, ...rest } = bar;
                        return { ...rest, time: realTime };
                    });
                    const seenReal = new Set(prevReal.map(b => b.time));
                    const unique = chunk.filter(b => !seenReal.has(b.time));
                    if (unique.length > 0) {
                        const merged = canonicalizeBars([...unique, ...prevReal]);
                        const compressed = compressGaps(merged, effectiveTimeframeRef.current);
                        const nextLen = compressed.length;
                        setHistoricalData(compressed);
                        historicalLenRef.current = nextLen;
                        if (compressed.length > 0) {
                            const lb = compressed[compressed.length - 1];
                            lastBarRef.current = { time: lb.time, time_real: lb.time_real ?? lb.time };
                        }
                        // Re-hydrate indicators to cover the enlarged window (use same unique_id)
                        if (nextLen > lastIndicatorHydrationLenRef.current) {
                            try {
                                const items = activeIndicatorsRef.current;
                                const prevOldest = oldest; // previous left-edge timestamp
                                for (const ind of items) {
                                    const uid = getIndicatorKey(ind.name, ind.params, symbol, timeframe, ind.paneType);
                                    sendIndicatorAction('add_indicator', {
                                        indicator: ind.name,
                                        params: ind.params,
                                        unique_id: uid,
                                        before: prevOldest,
                                        limit: unique.length,
                                        pane_type: ind.paneType,
                                        scale_type: ind.scaleType,
                                    });
                                }
                                lastIndicatorHydrationLenRef.current = nextLen;
                            } catch (e) {
                                console.warn('Failed to re-request indicators for extended window:', e);
                            }
                        }
                        return { added: unique.length, total: nextLen, hasMore: !!json?.page?.has_more };
                    }
                }
                return { added: 0, total: prevLen, hasMore: !!json?.page?.has_more };
            } catch (e) {
                console.warn('Cursor-based prepend failed; keeping current chart history:', e);
                return { added: 0, total: prevLen };
            } finally {
                if (loadMoreRequestIdRef.current === requestId) loadMoreInFlightRef.current = false;
            }
        }

        // Fallback: increase count and replace
        const nextCount = Math.max(prevLen + step, (lastRequestedCountRef.current || 0) + step);
        if (lastRequestedCountRef.current && nextCount <= lastRequestedCountRef.current) {
            return { added: 0, total: prevLen };
        }
        const requestId = loadMoreRequestIdRef.current + 1;
        loadMoreRequestIdRef.current = requestId;
        loadMoreInFlightRef.current = true;
        try {
            const params = new URLSearchParams({ account_id: accountId, symbol, resolution: timeframe, count: String(nextCount) });
            const json = await fetchChartHistoryJson(buildChartHistoryUrl(params), token);
            if (!isRequestCurrent() || loadMoreRequestIdRef.current !== requestId) {
                return { added: 0, total: prevLen };
            }
            effectiveTimeframeRef.current = resolveEffectiveTimeframe(timeframe, json?.served_timeframe || effectiveTimeframeRef.current);
            const raw = Array.isArray(json?.candles) ? json.candles : [];
            const formatted = canonicalizeBars(raw
                .map(d => ({
                    time: toSeconds(d.time),
                    open: d.open,
                    high: d.high,
                    low: d.low,
                    close: d.close,
                    volume: (Number.isFinite(d.volume) ? Number(d.volume) : (Number.isFinite(d.tick_volume) ? Number(d.tick_volume) : (Number.isFinite(d.tickVolume) ? Number(d.tickVolume) : undefined))),
                    tickVolume: (Number.isFinite(d.tick_volume) ? Number(d.tick_volume) : (Number.isFinite(d.tickVolume) ? Number(d.tickVolume) : undefined)),
                }))
                .filter(d => typeof d.time === 'number'));
            if (formatted.length > prevLen) {
                const compressed = compressGaps(formatted, effectiveTimeframeRef.current);
                setHistoricalData(compressed);
                lastRequestedCountRef.current = formatted.length;
                historicalLenRef.current = compressed.length;
                if (compressed.length > 0) {
                    const lb = compressed[compressed.length - 1];
                    lastBarRef.current = { time: lb.time, time_real: lb.time_real ?? lb.time };
                }
                // Re-hydrate indicators for full window size
                if (formatted.length > lastIndicatorHydrationLenRef.current) {
                    try {
                        const items = activeIndicatorsRef.current;
                        for (const ind of items) {
                            const uid = getIndicatorKey(ind.name, ind.params, symbol, timeframe, ind.paneType);
                            if (ind.backendUniqueId) {
                                sendIndicatorAction('remove_indicator', { unique_id: ind.backendUniqueId });
                            }
                            sendIndicatorAction('add_indicator', {
                                indicator: ind.name,
                                params: ind.params,
                                unique_id: uid,
                                count: Math.max(1000, formatted.length),
                                pane_type: ind.paneType,
                                scale_type: ind.scaleType,
                            });
                        }
                        lastIndicatorHydrationLenRef.current = formatted.length;
                    } catch (e) {
                        console.warn('Failed to re-request indicators for full window:', e);
                    }
                }
                return { added: formatted.length - prevLen, total: formatted.length };
            }
            lastRequestedCountRef.current = Math.max(lastRequestedCountRef.current || 0, nextCount);
            return { added: 0, total: prevLen };
        } catch (e2) {
            console.error('Count-growth fallback failed:', e2);
            return { added: 0, total: prevLen };
        } finally {
            if (loadMoreRequestIdRef.current === requestId) loadMoreInFlightRef.current = false;
        }
    }, [accountId, symbol, timeframe, historicalData, sendIndicatorAction]);

    const value = useMemo(() => ({
        isConnected,
        candle,
        bid,
        ask,
        historicalData,
        activeIndicators,
        setActiveIndicators,
        historyStatus,
        historyError,
        retryHistory,
        removeActiveIndicator,
        availableIndicators,
        sendIndicatorAction,
        indicatorLiveUpdate,
        isLoggingEnabled,
        toggleLogging,
        loadMoreHistory,
        // Nested spec streams
        specStreams,
        subscribeSpec,
        unsubscribeSpec,
    }), [
        isConnected,
        candle,
        bid,
        ask,
        historicalData,
        activeIndicators,
        historyStatus,
        historyError,
        retryHistory,
        removeActiveIndicator,
        availableIndicators,
        sendIndicatorAction,
        indicatorLiveUpdate,
        isLoggingEnabled,
        toggleLogging,
        loadMoreHistory,
        specStreams,
        subscribeSpec,
        unsubscribeSpec,
    ]);
    return (
        <PriceFeedContext.Provider value={value}>
            {children}
        </PriceFeedContext.Provider>
    );
}

export default PriceFeedProvider;
